import $ from 'jquery';
import moment from 'moment';

import {assert} from 'Asserts';
import {DNA_COMPLEMENT_TABLE} from 'biojs/BioConstants';
import * as util from 'biojs/Util';
import {
  ASSEMBLY_STEP,
  CRISPRAnalysis,
  CRISPRSequence,
  CRISPRVector,
  VECTOR_SOURCE_TYPE,
} from 'constants/CRISPRConstants';
import {EventConstants} from 'constants/EventConstants';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import * as FileBrowserDAO from 'file-browser/FileBrowserDAO';
import FileStates from 'FileStates';
import {BioentitySummary} from 'folder-items/FileConstants';
import AppDispatcher from 'lib/Dispatcher';
import * as CRISPRAssemblyDAO from 'molbio/crispr/assembly/CRISPRAssemblyDAO';
import CRISPRAssemblyStore, {CRISPRAssemblyState} from 'molbio/crispr/assembly/CRISPRAssemblyStore';
import * as CRISPRVectorDAO from 'molbio/crispr/assembly/CRISPRVectorDAO';
import {CreateVectorData} from 'molbio/crispr/assembly/CRISPRVectorDAO';
import * as sequtil from 'molbio/SeqUtil';
import listFolderSection from 'nav/sidepanel/listFolderSection';
import {EntrySummary} from 'notebook/ELNConstants';
import * as EntryDayOps from 'notebook/entries/EntryDayOps';
import notifications from 'notifications';

function _checkInsertionRegion(
  assemblyCid: string,
  {start, end}: {start: number | null; end: number | null},
  sequence: CRISPRSequence
): boolean {
  if (start == null || end == null) {
    notifications.error('Specify insertion region start and end to continue.');
    return false;
  }

  if (!sequtil.isValidRange(start, end, sequence.length, sequence.circular)) {
    notifications.error('Insertion region is invalid.');
    return false;
  }
  return true;
}

const CRISPRAssemblyActions = {
  getAllVectors(): void {
    AppDispatcher.dispatch({actionType: EventConstants.CRISPR_VECTOR_GET_ALL});
    CRISPRVectorDAO.getAll()
      .done((response) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.CRISPR_VECTOR_GET_ALL_COMPLETED,
          vectors: response!.data,
        });
      })
      .fail((response) => {
        notifications.notifyError(response);
        AppDispatcher.dispatch({actionType: EventConstants.CRISPR_VECTOR_GET_ALL_FAILED});
      });
  },

  __createVector(data: CreateVectorData): JQueryPromise<CRISPRVector> {
    AppDispatcher.dispatch({actionType: EventConstants.CRISPR_VECTOR_CREATE});
    return CRISPRVectorDAO.create(data)
      .done((vector) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.CRISPR_VECTOR_CREATE_COMPLETED,
          vector,
        });
      })
      .fail((response) => {
        notifications.notifyError(response);
        AppDispatcher.dispatch({actionType: EventConstants.CRISPR_VECTOR_CREATE_FAILED});
      });
  },

  __updateVector(vectorId: string, data: {}): JQueryPromise<CRISPRVector> {
    AppDispatcher.dispatch({actionType: EventConstants.CRISPR_VECTOR_UPDATE});
    return CRISPRVectorDAO.update(vectorId, data)
      .done((vector) => {
        AppDispatcher.dispatch({
          actionType: EventConstants.CRISPR_VECTOR_UPDATE_COMPLETED,
          vector,
        });
      })
      .fail((response) => {
        notifications.notifyError(response);
        AppDispatcher.dispatch({actionType: EventConstants.CRISPR_VECTOR_UPDATE_FAILED});
      });
  },

  createAssembly(assemblyCid: string, analysis: CRISPRAnalysis, assemblies: Array<unknown>): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CRISPR_ASSEMBLY_CREATE_ASSEMBLY,
      analysis,
      assemblyCid,
      assemblies,
    });
  },

  updateAssembly(assemblyCid: string, data: Partial<CRISPRAssemblyState>): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CRISPR_ASSEMBLY_UPDATE_ASSEMBLY,
      assemblyCid,
      data,
    });
  },

  selectVector(assemblyCid: string, vectorId: string): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CRISPR_ASSEMBLY_SELECT_VECTOR,
      assemblyCid,
      vectorId,
    });
  },

  selectSequence(assemblyCid: string, sequence: CRISPRSequence): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CRISPR_ASSEMBLY_SELECT_SEQUENCE,
      assemblyCid,
      sequence,
    });
  },

  selectExistingSequence(assemblyCid: string, sequence: CRISPRSequence | null): void {
    AppDispatcher.dispatch({
      actionType: EventConstants.CRISPR_ASSEMBLY_SELECT_EXISTING_SEQUENCE,
      assemblyCid,
      sequence,
    });
  },

  finalizeSelectedVector(assemblyCid: string): void {
    let vector;
    const {
      biojsSequence,
      enzyme,
      existingSequenceInputState,
      fileInputState,
      insertionRegion,
      vectorSelectorState,
      vectorSourceType,
    } = CRISPRAssemblyStore.getAssemblyState(assemblyCid);
    let sequencePromise = null;
    let vectorPromise = null;

    switch (vectorSourceType) {
      case VECTOR_SOURCE_TYPE.CREATE_FROM_FILE: {
        const {sequence} = fileInputState;

        if (sequence == null) {
          notifications.error('Upload a file to continue.');
          return;
        }

        if (!(fileInputState.sequenceName.trim().length > 0 && fileInputState.folderId != null)) {
          notifications.error('Name and folder must be specified.');
          return;
        }

        if (!_checkInsertionRegion(assemblyCid, insertionRegion, sequence)) {
          return;
        }

        assert(enzyme != null, 'Expected to receive an enzyme when finalizing CRISPR vector');
        assert(sequence.id === biojsSequence!.id, 'Expected sequence ID to match bioJS sequence ID');

        sequencePromise = FileBrowserDAO.updateApiIdentified(
          sequence.id,
          {name: fileInputState.sequenceName, folder: fileInputState.folderId},
          {parentFolderId: sequence.folder.api_identifier}
        );

        vectorPromise = CRISPRAssemblyActions.__createVector({
          sequence: sequence.id,
          insertionRegionStart: insertionRegion.start!,
          insertionRegionEnd: insertionRegion.end!,
        });
        break;
      }

      case VECTOR_SOURCE_TYPE.SELECT_EXISTING_SEQUENCE: {
        const {sequence} = existingSequenceInputState;
        if (sequence == null) {
          notifications.error('Select a sequence to continue.');
          return;
        }
        if (!_checkInsertionRegion(assemblyCid, insertionRegion, sequence)) {
          return;
        }
        assert(enzyme != null, 'Expected to receive an enzyme when finalizing CRISPR vector');
        assert(sequence.id === biojsSequence!.id, 'Expected sequence ID to match bioJS sequence ID');
        vectorPromise = CRISPRAssemblyActions.__createVector({
          sequence: sequence.id,
          insertionRegionStart: insertionRegion.start!,
          insertionRegionEnd: insertionRegion.end!,
        });
        break;
      }

      case VECTOR_SOURCE_TYPE.SELECT_VECTOR:
        ({vector} = vectorSelectorState);

        if (vector == null) {
          notifications.error('Choose a vector to continue.');
          return;
        }

        if (!_checkInsertionRegion(assemblyCid, insertionRegion, vector.sequence)) {
          return;
        }

        assert(enzyme != null, 'Expected to receive an enzyme when finalizing CRISPR vector');
        assert(vector.sequence.id === biojsSequence!.id, 'Expected sequence ID to match bioJS sequence ID');

        vectorPromise = CRISPRAssemblyActions.__updateVector(vector.id, {
          insertionRegionStart: insertionRegion.start,
          insertionRegionEnd: insertionRegion.end,
        });
        break;
      default:
        break;
    }

    CRISPRAssemblyActions.updateAssembly(assemblyCid, {finalizingSelectedVector: true});

    $.when<Promise<BioentitySummary> | CRISPRVector | null>(sequencePromise, vectorPromise)
      .done(() => {
        CRISPRAssemblyActions.updateAssembly(assemblyCid, {
          assemblyData: {
            vector: biojsSequence!,
            enzyme: enzyme!,
            insertionRegion: {start: insertionRegion.start!, end: insertionRegion.end!},
          },
          step: ASSEMBLY_STEP.FINALIZE_ASSEMBLIES,
        });
      })
      .fail(notifications.notifyError)
      .always(() => {
        CRISPRAssemblyActions.updateAssembly(assemblyCid, {finalizingSelectedVector: false});
      });
  },

  finalizeAssemblies(assemblyCid: string): void {
    let makeForward: (
      bases: string,
      insertPos: number,
      length: number
    ) => {bases: string; start: number; end: number};
    let makeReverse: (
      bases: string,
      insertPos: number,
      length: number
    ) => {bases: string; start: number; end: number};
    const {analysis, assemblies, assembliesFolder, assemblyData} =
      CRISPRAssemblyStore.getAssemblyState(assemblyCid);

    if (assemblies.length === 0) {
      notifications.error('No assemblies to make. Click the "Add" button.');
      return;
    }

    for (let i = 0; i < assemblies.length; i++) {
      const assembly = assemblies[i];
      if (assembly.bases!.length === 0) {
        notifications.error(`Assembly #${i + 1} has no bases!`);
        return;
      }
      if (assembly.name!.length === 0) {
        notifications.error(`Assembly #${i + 1} has no name!`);
        return;
      }
    }

    if (assembliesFolder == null) {
      notifications.error('Select a folder for the assemblies.');
      return;
    }

    const originalVector = assemblyData.vector;
    const offset = assemblyData.enzyme.get('offset');
    const {start, end} = assemblyData.insertionRegion;
    if (offset > 0) {
      makeForward = (bases: string, insertPos: number, length: number) => ({
        bases: originalVector.residueSlice(start - offset, start) + bases,
        start: sequtil.mod(insertPos - offset, length),
        end: sequtil.mod(insertPos + bases.length, length),
      });
      makeReverse = (bases: string, insertPos: number, length: number) => ({
        bases: util.revcomp(bases + originalVector.residueSlice(end, end + offset), DNA_COMPLEMENT_TABLE),
        start: insertPos,
        end: sequtil.mod(insertPos + bases.length + offset, length),
      });
    } else {
      makeForward = (bases: string, insertPos: number, length: number) => ({
        bases: bases + originalVector.residueSlice(end, end - offset),
        start: insertPos,
        end: sequtil.mod(insertPos + bases.length - offset, length),
      });
      makeReverse = (bases: string, insertPos: number, length: number) => ({
        bases: originalVector.residueSlice(start + offset, start) + bases,
        start: sequtil.mod(insertPos + offset, length),
        end: sequtil.mod(insertPos + bases.length, length),
      });
    }

    const assembledVectors = assemblies.map(({name, bases}) => {
      let addedG;
      const vector = originalVector.clone();

      if (CRISPRAssemblyStore.shouldPrependG(analysis) && bases!.charAt(0).toUpperCase() !== 'G') {
        bases = `G${bases}`;
        addedG = true;
      }

      const insertPos = vector.replaceBases(start, end, bases!);

      const sequencePrimers = vector.sequencePrimers();
      sequencePrimers.add({
        ...makeForward(bases!, insertPos, vector.length()),
        strand: 1,
        name: `${name} FWD`,
        folder_api_id: assembliesFolder,
      });
      sequencePrimers.add({
        ...makeReverse(bases!, insertPos, vector.length()),
        strand: -1,
        name: `${name} REV`,
        folder_api_id: assembliesFolder,
      });

      vector.annotations().add({
        start: insertPos + (addedG ? 1 : 0),
        end: insertPos + bases!.length,
        strand: 0,
        name: 'Guide',
      });
      // @ts-expect-error folderId is not expected in SequenceAttributes
      vector.set({name, folderId: assembliesFolder, circular: true});
      return vector;
    });

    CRISPRAssemblyActions.updateAssembly(assemblyCid, {finalizingAssemblies: true});

    const vectorPromises = assembledVectors.map((vector) => vector.save());

    $.when(...(vectorPromises || []))
      .done(() => {
        const vector = assembledVectors[0];
        notifications.success(`Saved ${assemblies.length} assemblies in "${vector.folder()!.name}".`);
        listFolderSection(vector.folder()!, FileBrowserConstants.INVENTORY_SECTION);
        CRISPRAssemblyActions.updateAssembly(assemblyCid, {
          step: ASSEMBLY_STEP.COMPLETED_ASSEMBLY,
          assemblyPrimers: assembledVectors
            .map((assembledVector) =>
              assembledVector
                .sequencePrimers()
                .map((primer) => ({name: primer.name(), bases: primer.bases()}))
            )
            .flat(),
          assembledVectorIds: assembledVectors.map((assembledVector) => assembledVector.id),
          assembliesRootFolder: vector.folder()!.source!.rootFolder.api_identifier,
        });
      })
      .fail(notifications.notifyError)
      .always(() => {
        CRISPRAssemblyActions.updateAssembly(assemblyCid, {finalizingAssemblies: false});
      });
  },

  createEntry(assemblyCid: string, sequenceName: string): void {
    const {assemblyPrimers, assembliesRootFolder, assembledVectorIds} =
      CRISPRAssemblyStore.getAssemblyState(assemblyCid);
    CRISPRAssemblyDAO.createEntry({
      assemblyPrimers: assemblyPrimers!,
      folder: assembliesRootFolder,
      initialDay: EntryDayOps.formatDate(moment()),
      name: `${sequenceName} Assemblies`,
      assembledVectorIds: assembledVectorIds!,
    })
      .done(({id}: EntrySummary) => {
        FileStates.get().openFolderItem(id, {open: true, source: 'crispr-assembly-entry'});
      })
      .fail(notifications.notifyError);
  },
};

export default CRISPRAssemblyActions;
